import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { appCopy } from '@zibanu/shared';
import { IconButton } from './basics';

export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 'alertdialog' for a confirmation (Remove). */
  role?: 'dialog' | 'alertdialog';
  /** The actions, kept at the bottom in thumb reach. */
  footer?: ReactNode;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A sheet over the screen: from the bottom on a phone, centred on a wider
 * screen. Named by its title; Escape and the backdrop close it; focus moves
 * in, stays in, and returns to whatever opened it.
 */
export function Sheet({ open, title, onClose, children, role = 'dialog', footer }: SheetProps) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    // A field marked data-autofocus (the capture box) goes first; otherwise the first control.
    const first = panel.current?.querySelector<HTMLElement>('[data-autofocus]') ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const keys = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close.current();
      }
      if (e.key === 'Tab' && panel.current) {
        const all = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
        if (all.length === 0) return;
        const [head, tail] = [all[0]!, all[all.length - 1]!];
        if (e.shiftKey && document.activeElement === head) {
          e.preventDefault();
          tail.focus();
        } else if (!e.shiftKey && document.activeElement === tail) {
          e.preventDefault();
          head.focus();
        }
      }
    };
    document.addEventListener('keydown', keys);
    return () => {
      document.removeEventListener('keydown', keys);
      document.body.style.overflow = overflow;
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="zb-sheet">
      {/* For a mouse or a thumb only: the X and Escape close it for everyone else, so screen readers hear one Close. */}
      <div className="zb-sheet__backdrop" aria-hidden="true" data-testid="zb-sheet-backdrop" onClick={onClose} />
      <div ref={panel} role={role} aria-modal="true" aria-labelledby={titleId} className="zb-sheet__panel" tabIndex={-1}>
        <div className="zb-sheet__grab" aria-hidden="true" />
        <div className="zb-sheet__top">
          <h2 id={titleId} className="zb-sheet__title">
            {title}
          </h2>
          <IconButton icon="x" label={appCopy.close()} onClick={onClose} />
        </div>
        {children}
        {footer && <div className="zb-sheet__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** A confirmation (spec 3A §7.7): a Sheet that is an alert dialog. */
export function Dialog(props: Omit<SheetProps, 'role'>) {
  return <Sheet {...props} role="alertdialog" />;
}

export interface ToastProps {
  message: string;
  action?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

/** A short message that floats 32 px clear of the Capture disc ("Update ready · Reload"). */
export function Toast({ message, action, onAction, onDismiss }: ToastProps) {
  return (
    <div role="status" className="zb-toast">
      <span className="zb-toast__text">{message}</span>
      {action && (
        <button type="button" className="zb-link-btn" onClick={onAction}>
          {action}
        </button>
      )}
      {onDismiss && <IconButton icon="x" label={appCopy.dismiss()} onClick={onDismiss} />}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  children?: ReactNode;
  action?: string;
  onAction?: () => void;
}

/** Nothing here yet: say what to do next. */
export function EmptyState({ title, children, action, onAction }: EmptyStateProps) {
  return (
    <div className="zb-empty">
      <p className="zb-empty__title">{title}</p>
      {children}
      {action && (
        <button type="button" className="zb-link-btn" onClick={onAction}>
          {action}
        </button>
      )}
    </div>
  );
}
